import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { driverVerificationAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import CameraModalWrapper from "../components/CameraModalWrapper.jsx";
import LoginModal from "../components/LoginModal.jsx";
import SignupModal from "../components/SignupModal.jsx";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { validate as validateEmail } from "email-validator";
import LocalStorageUtils, { STORAGE_KEYS } from "../utils/localStorage.utils.js";
import FileUtils from "../utils/files.utils.js";
import DummyDataButton from "../components/shared/DummyDataButton.jsx";

import "../styles/forms/index.css";

import "../styles/auth.css";
import { extractDetailFromError } from "../utils/errorHandler.js";
import {
   ERR_MISSING_DRIVER_ID,
   ERR_VERIFY_LOCATION_FIRST,
   ERR_EMPTY_FIELDS_REQUIRED,
   ERR_UPLOAD_REQUIRED_DOCS,
   ERR_EMAIL_REQUIRED,
   ERR_EMAIL_INVALID,
   ERR_PHONE_INVALID,
   ERR_SMS_CONSENT_REQUIRED,
   ERR_DATA_CONSENT_REQUIRED,
   ERR_SUBMIT_FAILED,
   ERR_DUPLICATE_SUBMISSION,
   ERR_SUBMISSION_LIMIT,
   GEO_NOT_SUPPORTED,
   GEO_PERMISSION_DENIED,
   GEO_UNAVAILABLE,
   GEO_TIMEOUT,
   GEO_HTTPS_REQUIRED,
} from "../utils/errorMessages.js";

const DriverVerificationForm = () => {
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const driverId = searchParams.get("driverId");
   const formContainerRef = useRef();

   const { isAuthenticated, user, isLoading, signOut } = useAuth();

   // Modal state management (moved from auth context)
   const [showLoginModal, setShowLoginModal] = useState(false);
   const [showSignupModal, setShowSignupModal] = useState(false);

   const [formData, setFormData] = useState({
      driverFirstName: "",
      driverLastName: "",
      driverEmail: "",
      driverPhone: "",
      locationMessage: "",
      isEmpty: "",
      emptyTime: "",
      emptyLocation: "",
      smsConsent: false,
      consent: false,
      driverId: driverId || "",
   });

   const [files, setFiles] = useState({
      truckPhoto: null,
      cabCard: null,
   });

   // Add state for existing image URLs
   const [existingImages, setExistingImages] = useState({
      truckPhoto: null,
      cabCard: null,
   });

   const [showEmptyFields, setShowEmptyFields] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [error, setError] = useState("");
   const [locationStatus, setLocationStatus] = useState("");
   const [locationError, setLocationError] = useState(false);
   const [locationSuccess, setLocationSuccess] = useState(false);
   const [locationAttempted, setLocationAttempted] = useState(false);
   const [activeCameraField, setActiveCameraField] = useState(null);
   const [showCameraModal, setShowCameraModal] = useState(false);
   const [authenticationSkipped, setAuthenticationSkipped] = useState(false);
   const [isFormDataLoaded, setIsFormDataLoaded] = useState(false);

   // Load form data from localStorage on component mount
   const loadFormDataFromStorage = useCallback(async () => {
      try {
         const {
            formData: savedFormData,
            filesInfo: savedFilesInfo,
            locationData: savedLocationData,
         } = LocalStorageUtils.loadFormData();

         if (savedFormData) {
            const updatedFormData = {
               ...formData,
               ...savedFormData,
               driverId: driverId || savedFormData.driverId || "",
            };
            setFormData(updatedFormData);
         }

         // Load files from localStorage
         if (savedFilesInfo) {
            const restoredFiles = await LocalStorageUtils.loadFilesFromStorage();
            setFiles(restoredFiles);
         }

         if (savedLocationData) {
            setLocationStatus(savedLocationData.status || "");
            setLocationError(savedLocationData.error || false);
            setLocationSuccess(savedLocationData.success || false);
            setLocationAttempted(savedLocationData.attempted || false);
         }
      } catch (error) {
         console.error("Error loading form data from localStorage:", error);
      }
   }, [driverId]);

   // Save form data to localStorage
   const saveFormDataToStorage = useCallback(
      (data) => {
         try {
            LocalStorageUtils.add(STORAGE_KEYS.FORM_DATA, data);

            // Save location data separately
            const locationData = {
               status: locationStatus,
               error: locationError,
               success: locationSuccess,
               attempted: locationAttempted,
            };
            LocalStorageUtils.add(STORAGE_KEYS.LOCATION_DATA, locationData);
         } catch (error) {
            console.error("Error saving form data to localStorage:", error);
         }
      },
      [locationStatus, locationError, locationSuccess, locationAttempted]
   );

   // Clear all form data from localStorage
   const clearFormDataFromStorage = useCallback(() => {
      try {
         LocalStorageUtils.clearFormData();
      } catch (error) {
         console.error("Error clearing localStorage:", error);
      }
   }, []);

   const getAutoPopulateData = useCallback(() => {
      if (!user) return {};

      const data = {};
      if (user.email) data.driverEmail = user.email;
      if (user.phone) data.driverPhone = user.phone;
      if (user.first_name) data.driverFirstName = user.first_name;
      if (user.last_name) data.driverLastName = user.last_name;

      return data;
   }, [user]);

   const getExistingImages = useCallback(() => {
      if (!user) return {};

      const images = {};
      if (user.truck_photo_url) images.truckPhoto = user.truck_photo_url;
      if (user.cab_card_url) images.cabCard = user.cab_card_url;

      return images;
   }, [user]);

   // Modal controls
   const openLoginModal = useCallback(() => setShowLoginModal(true), []);
   const closeLoginModal = useCallback(() => setShowLoginModal(false), []);
   const openSignupModal = useCallback(() => setShowSignupModal(true), []);
   const closeSignupModal = useCallback(() => setShowSignupModal(false), []);

   // Authentication handlers
   const handleLoginSuccess = useCallback((userData) => {
      setShowLoginModal(false);
   }, []);

   const handleSignupSuccess = useCallback((userData) => {
      setShowSignupModal(false);
   }, []);

   const handleSkipAccount = useCallback(() => {
      setShowSignupModal(false);
   }, []);

   useEffect(() => {
      setShowEmptyFields(formData.isEmpty === "No");
   }, [formData.isEmpty]);

   useEffect(() => {
      if (!driverId) {
         setError(ERR_MISSING_DRIVER_ID);
      }
   }, [driverId]);

   // Load form data from localStorage on mount
   useEffect(() => {
      const loadData = async () => {
         await loadFormDataFromStorage();
         setIsFormDataLoaded(true);
      };
      loadData();
   }, [loadFormDataFromStorage]);

   // Auto-populate form data when user is authenticated (with priority over localStorage)
   useEffect(() => {
      if (isAuthenticated && user && isFormDataLoaded) {
         const autoPopulateData = getAutoPopulateData();
         const existingImageUrls = getExistingImages();

         setFormData((prev) => ({ ...prev, ...autoPopulateData }));
         setExistingImages((prev) => ({ ...prev, ...existingImageUrls }));
      }
   }, [isAuthenticated, user, getAutoPopulateData, getExistingImages, isFormDataLoaded]);

   // Save form data to localStorage whenever formData changes (but not during initial load)
   useEffect(() => {
      if (isFormDataLoaded && Object.keys(formData).length > 0) {
         saveFormDataToStorage(formData);
      }
   }, [formData, saveFormDataToStorage, isFormDataLoaded]);

   // Save files to localStorage whenever files change (but not during initial load)
   useEffect(() => {
      if (isFormDataLoaded && Object.keys(files).length > 0) {
         const saveFiles = async () => {
            try {
               await LocalStorageUtils.saveFilesToStorage(files);
            } catch (error) {
               console.error("Error saving files to localStorage:", error);
            }
         };
         saveFiles();
      }
   }, [files, isFormDataLoaded]);

   const handleInputChange = (e) => {
      const { name, value, type, checked } = e.target;

      setFormData((prev) => ({
         ...prev,
         [name]: type === "checkbox" ? checked : value,
      }));
   };

   // Handle filling form with dummy data
   const handleFillDummyData = (dummyData) => {
      setFormData((prev) => ({
         ...prev,
         ...dummyData,
      }));

      // Set location status to simulate successful location verification
      setLocationStatus("Location verified! (Lat: 40.7128, Lng: -74.0060)");
      setLocationSuccess(true);
      setLocationError(false);
      setLocationAttempted(true);

      // Set showEmptyFields based on dummy data
      setShowEmptyFields(dummyData.isEmpty === "No");
   };

   const handlePhoneChange = (value) => {
      // react-phone-number-input returns the value in E.164 format (+12133734253)
      // or undefined if invalid
      setFormData((prev) => ({
         ...prev,
         driverPhone: value || "",
      }));
   };

   const handleFileChange = (e) => {
      const { name, files: fileList } = e.target;
      if (fileList && fileList[0]) {
         setFiles((prev) => ({
            ...prev,
            [name]: fileList[0],
         }));
      }
   };

   const openCamera = (fieldName) => {
      setActiveCameraField(fieldName);
      setShowCameraModal(true);
   };

   const handleCameraCapture = (blob) => {
      if (activeCameraField && blob) {
         const fileName = `${activeCameraField}.jpg`;
         const capturedFile = new File([blob], fileName, { type: "image/jpeg" });
         setFiles((prev) => ({
            ...prev,
            [activeCameraField]: capturedFile,
         }));
         setShowCameraModal(false);
         setActiveCameraField(null);
      }
   };

   const closeCameraModal = () => {
      setShowCameraModal(false);
      setActiveCameraField(null);
   };

   // Handle skipping authentication and proceed with form submission
   const handleSkipAccountAndSubmit = () => {
      handleSkipAccount(); // Close the modal
      setAuthenticationSkipped(true);
      // Trigger form submission after a short delay to let state update
      setTimeout(() => {
         const submitButton = document.querySelector(".submit-button");
         if (submitButton) submitButton.click();
      }, 100);
   };

   const getLocation = () => {
      setLocationStatus("Locating...");
      setLocationError(false);
      setLocationSuccess(false);
      setLocationAttempted(true); // Mark that location attempt was made

      if (!navigator.geolocation) {
         const message = GEO_NOT_SUPPORTED;
         setLocationStatus(message);
         setLocationError(true);
         setFormData((prev) => ({
            ...prev,
            locationMessage: message,
         }));
         return;
      }

      navigator.geolocation.getCurrentPosition(
         (position) => {
            const locationMessage = `Location verified! (Lat: ${position.coords.latitude.toFixed(
               5
            )}, Lng: ${position.coords.longitude.toFixed(5)})`;
            setFormData((prev) => ({
               ...prev,
               locationMessage: locationMessage,
            }));
            setLocationStatus(locationMessage);
            setLocationSuccess(true);
         },
         (error) => {
            let message = GEO_UNAVAILABLE;
            if (error.code === 1) {
               message = GEO_PERMISSION_DENIED;
            } else if (error.code === 2) {
               message = GEO_UNAVAILABLE;
            } else if (error.code === 3) {
               message = GEO_TIMEOUT;
            } else if (
               window.location.protocol !== "https:" &&
               window.location.hostname !== "localhost"
            ) {
               message = GEO_HTTPS_REQUIRED;
            }
            setLocationStatus(message);
            setLocationError(true);
            // Set location message
            setFormData((prev) => ({
               ...prev,
               locationMessage: message,
            }));
         }
      );
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      setError("");

      if (!driverId) {
         setError(ERR_MISSING_DRIVER_ID);
         return;
      }

      if (!locationAttempted) {
         setError(ERR_VERIFY_LOCATION_FIRST);
         return;
      }

      if (formData.isEmpty === "No" && (!formData.emptyTime || !formData.emptyLocation)) {
         setError(ERR_EMPTY_FIELDS_REQUIRED);
         return;
      }

      if (
         (!files.truckPhoto && !existingImages.truckPhoto) ||
         (!files.cabCard && !existingImages.cabCard)
      ) {
         setError(ERR_UPLOAD_REQUIRED_DOCS);
         return;
      }

      if (!formData.driverEmail) {
         setError(ERR_EMAIL_REQUIRED);
         return;
      }

      // Validate email format
      if (!validateEmail(formData.driverEmail)) {
         setError(ERR_EMAIL_INVALID);
         return;
      }

      // Validate phone number format
      if (!formData.driverPhone || !isValidPhoneNumber(formData.driverPhone)) {
         setError(ERR_PHONE_INVALID);
         return;
      }

      if (!formData.smsConsent) {
         setError(ERR_SMS_CONSENT_REQUIRED);
         return;
      }

      if (!formData.consent) {
         setError(ERR_DATA_CONSENT_REQUIRED);
         return;
      }

      // Check if user needs to authenticate first (unless they've skipped)
      if (!isAuthenticated && !authenticationSkipped) {
         openSignupModal();
         return;
      }

      setIsSubmitting(true);

      try {
         const submitData = {
            ...formData,
            smsConsent: formData.smsConsent ? "Yes" : "",
            truckPhoto: files.truckPhoto || null,
            cabCard: files.cabCard || null,
            truckPhotoUrl: existingImages.truckPhoto || "",
            cabCardUrl: existingImages.cabCard || "",
            driverId,
         };

         await driverVerificationAPI.submitForm(submitData);

         const timestamp = new Date().toISOString();
         const submissionData = {
            id: Date.now(),
            timestamp: timestamp,
            driverId,
            driverInfo: {
               firstName: formData.driverFirstName,
               lastName: formData.driverLastName,
               email: formData.driverEmail,
               phone: formData.driverPhone,
            },
            location: {
               locationMessage: formData.locationMessage,
            },
            status: {
               isEmpty: formData.isEmpty,
               emptyTime: formData.emptyTime,
               emptyLocation: formData.emptyLocation,
            },
            documents: {
               truckPhoto: files.truckPhoto ? files.truckPhoto.name : null,
               cabCard: files.cabCard ? files.cabCard.name : null,
            },
            consent: formData.smsConsent,
         };

         const existingSubmissions = JSON.parse(
            localStorage.getItem("driver_form_submissions") || "[]"
         );
         existingSubmissions.push(submissionData);
         localStorage.setItem(
            "driver_form_submissions",
            JSON.stringify(existingSubmissions, null, 2)
         );

         // Clear form data from localStorage on successful submission
         clearFormDataFromStorage();

         setIsSubmitting(false);
         setAuthenticationSkipped(false); // Reset skip state
         navigate("/thank-you?name=" + formData.driverFirstName);
      } catch (err) {
         console.error("Error submitting form:", err);
         //[TODO]: Errors are being parsed on front-end and back-end we should decide one place to sent the correct messages and just show to the end user
         const extractedError = extractDetailFromError(err);

         if (extractedError) setError(extractedError);

         if (err.response && err.response.data) {
            const errorData = err.response.data;
            if (errorData.detail) {
               if (typeof errorData.detail === "object") {
                  if (errorData.detail.error === "duplicate_submission") {
                     setError(ERR_DUPLICATE_SUBMISSION);
                  } else if (errorData.detail.error === "submission_limit_reached") {
                     setError(ERR_SUBMISSION_LIMIT);
                  } else {
                     setError(errorData.detail.message || ERR_SUBMIT_FAILED);
                  }
               } else {
                  setError(errorData.detail);
               }
            } else {
               setError(ERR_SUBMIT_FAILED);
            }
         } else {
            setError(ERR_SUBMIT_FAILED);
         }

         setIsSubmitting(false);
         setAuthenticationSkipped(false); // Reset skip state on error
      }
   };

   // Show loading screen while authenticating
   if (isLoading) {
      return (
         <div className="auth-loading-container">
            <div className="auth-loading-content">
               <div className="auth-loading-spinner"></div>
               <h3>Authenticating...</h3>
               <p>Please wait while we verify your session</p>
            </div>
         </div>
      );
   }

   return (
      <div className="form-container" ref={formContainerRef}>
         <div className="form-header-modern">
            <div className="form-header-background"></div>
            <div className="form-header-content-wrapper">
               <div className="form-header-main">
                  <div className="form-header-icon">
                     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                        />
                     </svg>
                  </div>
                  <div className="form-header-text">
                     <h1>Driver Verification</h1>
                     <p>Secure identity verification for freight transportation</p>
                  </div>
               </div>

               <div className="form-header-auth-section">
                  {isAuthenticated ? (
                     <div className="auth-user-info">
                        <div className="user-avatar">
                           <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth="2"
                                 d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                           </svg>
                        </div>
                        <div className="user-details">
                           <span className="user-greeting">Welcome back</span>
                           <span className="user-name">
                              {user?.first_name ||
                                 user?.firstName ||
                                 user?.emailOrPhone ||
                                 "Driver"}
                           </span>
                        </div>
                        <button className="logout-button" onClick={signOut} title="Logout">
                           <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth="2"
                                 d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                              />
                           </svg>
                        </button>
                     </div>
                  ) : (
                     <button className="login-button-enhanced" onClick={openLoginModal}>
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                           />
                        </svg>
                        Sign In
                     </button>
                  )}
               </div>
            </div>
         </div>

         <form onSubmit={handleSubmit} className="form-content">
            {/* Dummy Data Button */}
            <div className="form-section" style={{ paddingBottom: "10px" }}>
               <DummyDataButton formType="driverVerification" onFillData={handleFillDummyData} />
            </div>

            {/* Driver Information */}
            <div className="form-section">
               <h3 className="section-title">Driver Information</h3>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="driverFirstName">
                        FIRST NAME <span className="required">*</span>
                     </label>
                     <input
                        type="text"
                        id="driverFirstName"
                        name="driverFirstName"
                        value={formData.driverFirstName}
                        onChange={handleInputChange}
                        required
                     />
                  </div>
                  <div className="form-group">
                     <label htmlFor="driverLastName">
                        LAST NAME <span className="required">*</span>
                     </label>
                     <input
                        type="text"
                        id="driverLastName"
                        name="driverLastName"
                        value={formData.driverLastName}
                        onChange={handleInputChange}
                        required
                     />
                  </div>
               </div>
               <div className="form-row">
                  <div className="form-group">
                     <label htmlFor="driverEmail">
                        EMAIL ADDRESS <span className="required">*</span>
                     </label>
                     <input
                        type="email"
                        id="driverEmail"
                        name="driverEmail"
                        value={formData.driverEmail}
                        onChange={handleInputChange}
                        placeholder="driver@example.com"
                        required
                     />
                  </div>
                  <div className="form-group">
                     <label htmlFor="driverPhone">
                        CELL PHONE <span className="required">*</span>
                     </label>
                     <PhoneInput
                        id="driverPhone"
                        name="driverPhone"
                        value={formData.driverPhone}
                        onChange={handlePhoneChange}
                        placeholder="(555) 123-4567"
                        defaultCountry="US"
                        limitMaxLength={true}
                        required
                     />
                  </div>
               </div>
            </div>

            {/* Document Uploads */}
            <div className="form-section">
               <h3 className="section-title">Document Uploads</h3>

               <div
                  className={`file-upload-group ${
                     files.truckPhoto || existingImages.truckPhoto ? "has-file" : ""
                  }`}
               >
                  <label htmlFor="truckPhoto">
                     TRUCK PHOTO <span className="required">*</span>
                  </label>
                  <p className="helper-text">
                     Side of truck with DOT number & company name visible
                  </p>
                  <input
                     type="file"
                     id="truckPhoto"
                     name="truckPhoto"
                     onChange={handleFileChange}
                     accept="image/*,.pdf"
                     style={{ display: "none" }}
                  />

                  {/* Show existing image if available */}
                  {existingImages.truckPhoto && !files.truckPhoto && (
                     <div className="existing-image-preview">
                        <img
                           src={existingImages.truckPhoto}
                           alt="Previously uploaded truck documentation"
                           style={{
                              maxWidth: "200px",
                              maxHeight: "150px",
                              borderRadius: "8px",
                              marginBottom: "10px",
                           }}
                        />
                        <div className="existing-image-info">
                           <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              style={{
                                 display: "inline-block",
                                 verticalAlign: "middle",
                                 marginRight: "8px",
                                 color: "var(--success)",
                              }}
                           >
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth="2"
                                 d="M5 13l4 4L19 7"
                              />
                           </svg>
                           Previously uploaded truck photo
                        </div>
                     </div>
                  )}

                  <div className="upload-buttons">
                     <button
                        type="button"
                        className="upload-button camera-btn"
                        onClick={() => openCamera("truckPhoto")}
                     >
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                           />
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                           />
                        </svg>
                        {existingImages.truckPhoto ? "Retake Photo" : "Take Photo"}
                     </button>
                  </div>
                  {files.truckPhoto && (
                     <div className="file-info show">
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           width="16"
                           height="16"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor"
                           style={{
                              display: "inline-block",
                              verticalAlign: "middle",
                              marginRight: "8px",
                              color: "var(--success)",
                           }}
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                           />
                        </svg>
                        {files.truckPhoto.name} ({FileUtils.formatFileSize(files.truckPhoto.size)})
                     </div>
                  )}
               </div>

               <div
                  className={`file-upload-group ${
                     files.cabCard || existingImages.cabCard ? "has-file" : ""
                  }`}
               >
                  <label htmlFor="cabCard">
                     CAB CARD <span className="required">*</span>
                  </label>
                  <p className="helper-text">Commercial vehicle registration document</p>
                  <input
                     type="file"
                     id="cabCard"
                     name="cabCard"
                     onChange={handleFileChange}
                     accept="image/*,.pdf"
                     style={{ display: "none" }}
                  />

                  {/* Show existing image if available */}
                  {existingImages.cabCard && !files.cabCard && (
                     <div className="existing-image-preview">
                        <img
                           src={existingImages.cabCard}
                           alt="Previously uploaded cab card"
                           style={{
                              maxWidth: "200px",
                              maxHeight: "150px",
                              borderRadius: "8px",
                              marginBottom: "10px",
                           }}
                        />
                        <div className="existing-image-info">
                           <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              style={{
                                 display: "inline-block",
                                 verticalAlign: "middle",
                                 marginRight: "8px",
                                 color: "var(--success)",
                              }}
                           >
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth="2"
                                 d="M5 13l4 4L19 7"
                              />
                           </svg>
                           Previously uploaded cab card
                        </div>
                     </div>
                  )}

                  <div className="upload-buttons">
                     <button
                        type="button"
                        className="upload-button camera-btn"
                        onClick={() => openCamera("cabCard")}
                     >
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                           />
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                           />
                        </svg>
                        {existingImages.cabCard ? "Retake Photo" : "Take Photo"}
                     </button>
                  </div>
                  {files.cabCard && (
                     <div className="file-info show">
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           width="16"
                           height="16"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor"
                           style={{
                              display: "inline-block",
                              verticalAlign: "middle",
                              marginRight: "8px",
                              color: "var(--success)",
                           }}
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                           />
                        </svg>
                        {files.cabCard.name} ({FileUtils.formatFileSize(files.cabCard.size)})
                     </div>
                  )}
               </div>
            </div>
            {/* Location Verification */}
            <div className="form-section">
               <h3 className="section-title">Location Verification</h3>
               <div className="form-group">
                  <label>
                     CURRENT LOCATION <span className="required">*</span>
                  </label>
                  <button
                     type="button"
                     className={`location-button ${locationAttempted ? "attempted" : ""}`}
                     onClick={getLocation}
                  >
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        width="20"
                        height="20"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                     </svg>
                     {locationAttempted ? "Retry Location" : "Verify My Location"}
                  </button>
                  {locationStatus && (
                     <div
                        className={`location-status ${
                           locationSuccess ? "success" : ""
                        } ${locationError ? "error" : ""}`}
                     >
                        {locationStatus}
                     </div>
                  )}
               </div>
            </div>

            {/* Status & Reference */}
            <div className="form-section">
               <h3 className="section-title">Status & Reference</h3>
               <div className="form-group">
                  <label htmlFor="isEmpty">
                     ARE YOU EMPTY? <span className="required">*</span>
                  </label>
                  <select
                     id="isEmpty"
                     name="isEmpty"
                     value={formData.isEmpty}
                     onChange={handleInputChange}
                     required
                  >
                     <option value="">Select Option</option>
                     <option value="Yes">Yes</option>
                     <option value="No">No</option>
                  </select>
               </div>

               {showEmptyFields && (
                  <div className="form-row">
                     <div className="form-group">
                        <label htmlFor="emptyTime">
                           ESTIMATED EMPTY TIME <span className="required">*</span>
                        </label>
                        <input
                           type="text"
                           id="emptyTime"
                           name="emptyTime"
                           value={formData.emptyTime}
                           onChange={handleInputChange}
                           placeholder="e.g., 2:30 PM"
                           required={showEmptyFields}
                        />
                        <p className="helper-text">e.g., 2:30 PM</p>
                     </div>
                     <div className="form-group">
                        <label htmlFor="emptyLocation">
                           EMPTY LOCATION <span className="required">*</span>
                        </label>
                        <input
                           type="text"
                           id="emptyLocation"
                           name="emptyLocation"
                           value={formData.emptyLocation}
                           onChange={handleInputChange}
                           placeholder="Enter location where you will be empty"
                           required={showEmptyFields}
                        />
                     </div>
                  </div>
               )}
            </div>

            {/* Consent */}
            <div className="form-section">
               <h3 className="section-title">Consent</h3>

               <div className="form-group">
                  <div className="checkbox-wrapper">
                     <input
                        type="checkbox"
                        id="smsConsent"
                        name="smsConsent"
                        checked={formData.smsConsent}
                        onChange={handleInputChange}
                        required
                     />
                     <label htmlFor="smsConsent">
                        By providing your phone number, you agree to receive text messages from
                        Driverify. Message and data rates may apply. Message frequency varies.{" "}
                        <span className="required">*</span>
                     </label>
                  </div>
               </div>

               <div className="form-group">
                  <div className="checkbox-wrapper">
                     <input
                        type="checkbox"
                        id="consent"
                        name="consent"
                        checked={formData.consent}
                        onChange={handleInputChange}
                        required
                     />
                     <label htmlFor="consent">
                        I consent to the collection and use of my personal information, including my
                        driver's license, biometric data (e.g., facial recognition), and other
                        identifying details for the sole purpose of verifying my identity and
                        preventing fraud in connection with freight transportation. I understand
                        that this information may be securely stored and used by Driverify LLC and
                        its partners for identity verification and compliance purposes, and that I
                        have read and agree to the{" "}
                        <a
                           href="https://driverify.ai/privacy-policy"
                           target="_blank"
                           rel="noopener noreferrer"
                        >
                           Privacy Policy
                        </a>
                        . <span className="required">*</span>
                     </label>
                  </div>
               </div>

               {!isAuthenticated && (
                  <div className="info-note">
                     <svg className="info-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path
                           fillRule="evenodd"
                           d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                           clipRule="evenodd"
                        />
                     </svg>
                     <p>
                        After submitting this form, you'll receive a text message with a secure link
                        to submit your CDL and selfie. Please proceed with that step to complete
                        your identity verification.
                     </p>
                  </div>
               )}
            </div>

            <div className="form-section" style={{ paddingTop: 0 }}>
               {error && (
                  <div className="error-message-box">
                     <div className="error-icon">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                           />
                        </svg>
                     </div>
                     <div className="error-content">
                        <h4>Error</h4>
                        <p>{error}</p>
                     </div>
                  </div>
               )}

               <button type="submit" className="submit-button" disabled={isSubmitting || !driverId}>
                  {isSubmitting ? (
                     <div className="auth-loading">
                        <div className="auth-spinner"></div>
                        Submitting...
                     </div>
                  ) : (
                     "Submit Verification"
                  )}
               </button>
            </div>
         </form>

         {/* Camera Modal */}
         <CameraModalWrapper
            key={activeCameraField}
            isOpen={showCameraModal}
            onCapture={handleCameraCapture}
            onClose={closeCameraModal}
         />

         {/* Authentication Modals */}
         <LoginModal
            isOpen={showLoginModal}
            onClose={closeLoginModal}
            onLoginSuccess={handleLoginSuccess}
         />

         <SignupModal
            isOpen={showSignupModal}
            onClose={closeSignupModal}
            onSignupSuccess={handleSignupSuccess}
            onSkipAccount={handleSkipAccountAndSubmit}
            driverData={formData}
         />
      </div>
   );
};

export default DriverVerificationForm;
